import { parseOrderData } from "util/helpers";

export const NO_DEPARTMENT_LABEL = "No Department";

/** Same start-time extraction as OrderCollectionDate (service window string). */
export function formatServiceStartTime(serviceHour) {
  if (!serviceHour) return null;
  const startTime = String(serviceHour).split(" - ")[0];
  if (!startTime) return null;
  const match = startTime.match(/^(\d{1,2})(am|pm)$/i);
  if (match) {
    const hour = match[1];
    const period = match[2].toUpperCase();
    return `${hour}:00 ${period}`;
  }
  return startTime;
}

function parseTimeOntoDate(baseDate, timeStr) {
  if (!timeStr || !baseDate) return null;
  const trimmed = String(timeStr).trim();
  const tryParse = Date.parse(`${baseDate.toDateString()} ${trimmed}`);
  if (!Number.isNaN(tryParse)) return new Date(tryParse);
  return null;
}

/**
 * Matchable strings for search: Shopify customer name parts + shipping recipient.
 */
export function getOrderSearchableNames(order) {
  const names = [];
  if (order.customer) {
    const fn = (order.customer.first_name || "").trim();
    const ln = (order.customer.last_name || "").trim();
    const full = `${fn} ${ln}`.trim();
    if (full) names.push(full);
    if (ln) names.push(ln);
  }
  if (order.shipping_address?.name) {
    names.push(String(order.shipping_address.name).trim());
  }
  return [...new Set(names.filter(Boolean))];
}

/** Last name for sorting — buyer (customer), not shipping recipient. */
export function getOrderCustomerLastName(order) {
  if (order.customer?.last_name) {
    return String(order.customer.last_name).trim();
  }
  if (order.customer) {
    const fn = order.customer.first_name || "";
    const full = `${fn} ${order.customer.last_name || ""}`.trim();
    const parts = full.split(/\s+/).filter(Boolean);
    if (parts.length) return parts[parts.length - 1];
  }
  return "";
}

export function getOrderDepartments(order) {
  const types = (order.line_items || [])
    .map((li) => li?.product?.product_type)
    .filter(Boolean)
    .map((t) => String(t).trim())
    .filter(Boolean);
  const uniq = [...new Set(types)];
  return uniq.length ? uniq : [NO_DEPARTMENT_LABEL];
}

export function getPrimaryDepartment(order) {
  const depts = getOrderDepartments(order);
  return [...depts].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))[0];
}

/**
 * Single-line label aligned with OrderCollectionDate (time · date).
 */
export function getOrderReadyByLabel(order) {
  const { collectionDate, pickupTime } = parseOrderData(order);
  if (!collectionDate || isNaN(collectionDate.getTime())) {
    return "";
  }
  const serviceHour = order.service?.hour;
  const formattedServiceTime = formatServiceStartTime(serviceHour);
  const time =
    pickupTime ||
    formattedServiceTime ||
    collectionDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  return `${time} · ${collectionDate.toLocaleDateString()}`;
}

/**
 * Comparable millis for Ready By sort. Null when no collection date.
 */
export function getOrderReadyBySortValue(order) {
  const parsed = parseOrderData(order);
  if (
    !parsed.collectionDate ||
    isNaN(parsed.collectionDate.getTime())
  ) {
    return null;
  }
  let d = new Date(parsed.collectionDate.getTime());
  const { pickupTime } = parsed;
  if (pickupTime) {
    const adj = parseTimeOntoDate(d, pickupTime);
    if (adj) d = adj;
  } else {
    const fmt = formatServiceStartTime(order.service?.hour);
    if (fmt) {
      const adj = parseTimeOntoDate(d, fmt);
      if (adj) d = adj;
    }
  }
  return d.getTime();
}

export function orderNumberSortKey(order) {
  const n = +String(order.name || "").replace(/\D/g, "");
  return Number.isFinite(n) ? n : 0;
}
